import React, { useEffect } from 'react';
import { t } from '../i18n';

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Roter Bestätigen-Button für destruktive Aktionen */
  danger?: boolean;
  /**
   * 'auto' folgt dem Light/Dark-Mode (zinc-Klassen), 'dark' erzwingt das
   * dunkle Design (für die immer-dunklen Vollbild-Views wie den Scorer).
   */
  variant?: 'auto' | 'dark';
  onConfirm: () => void;
  onClose: () => void;
}

/**
 * Zentrierter, responsiver Bestätigungs-Dialog im DartCloud-Design —
 * Ersatz für window.confirm.
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = t('web.page.manage-entries-page.bestaetigen'),
  cancelLabel = t('web.component.registrations-manager.abbrechen'),
  danger = false,
  variant = 'auto',
  onConfirm,
  onClose,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const dark = variant === 'dark';
  const card = dark
    ? 'border-[#27272a] bg-[#131316] text-[#fafafa]'
    : 'border-zinc-800 bg-zinc-900 text-white';
  const text = dark ? 'text-[#9ca3af]' : 'text-gray-400';
  const cancel = dark
    ? 'border-[#3f3f46] text-[#d4d4d8] hover:border-[#3fced1]/60'
    : 'border-zinc-700 text-gray-300 hover:border-[#3fced1]/60';

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      role="alertdialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`w-full max-w-sm rounded-2xl border p-6 shadow-2xl ${card}`}>
        <h2 className="text-lg font-bold tracking-tight">{title}</h2>
        <div className={`mt-2 text-sm leading-relaxed ${text}`}>{message}</div>
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="confirm-dialog-cancel"
            className={`min-h-[44px] rounded-xl border text-sm font-semibold transition ${cancel}`}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              onConfirm();
              onClose();
            }}
            data-testid="confirm-dialog-confirm"
            className={`min-h-[44px] rounded-xl text-sm font-semibold transition ${
              danger
                ? 'bg-red-500/90 text-white hover:bg-red-500'
                : 'bg-[#3fced1] text-black hover:bg-[#35b5b8]'
            }`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmDialog;
